import { AppState } from "../../store";
import { useStoreSelector } from '../../hooks/useStoreFromContext';
import { Navigate } from "react-router";
import { ThemeToggleButton } from "../../components/common/ThemeToggleButton";

const LandingPage = () => {
  const user = useStoreSelector((s: AppState) => s.user);

  const features = [
    {
      title: 'Free Registration',
      description:
        'Create your account in seconds and manage restaurants, menus, and QR codes from one dashboard.',
      icon: '👤',
    },
    {
      title: 'Restaurant Creation',
      description:
        'Add restaurant details including website, phone number, address, and restaurant images.',
      icon: '🏪',
    },
    {
      title: 'Multilingual Menus',
      description:
        'Support up to 3 languages with automatic default language selection via QR code.',
      icon: '🌍',
    },
    {
      title: 'Instant QR Codes',
      description:
        'Generate QR codes instantly for every menu and share them with your guests.',
      icon: '📱',
    },
    {
      title: 'Menu Sections',
      description:
        'Organize menus with sections like breakfast, pizza, sushi, desserts, and more.',
      icon: '📚',
    },
    {
      title: 'Advanced Menu Items',
      description:
        'Add allergens, calories, spicy labels, vegan tags, pricing, weight, and images.',
      icon: '🍕',
    },
  ]

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
      <div className="min-h-screen bg-white text-slate-900 transition-colors duration-300 dark:bg-[#0f172a] dark:text-white">
        {/* SEO Metadata */}
        <title>
          GetEat — Free QR Code Menu Platform for Restaurants
        </title>

        <meta
          name="description"
          content="GetEat is a free QR Code menu platform for restaurants. Create multilingual digital menus with QR codes, menu sections, food images, allergens."
        />

        <meta
          name="keywords"
          content="free qr code, free qr code generator, restaurant qr code, digital menu platform, multilingual menu, restaurant software, menu qr code generator, qr code menu creator, geteat"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="GetEat — Free QR Code Menu Platform"
        />

        <meta
          property="og:description"
          content="Create beautiful multilingual QR code menus for restaurants completely free."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geteat.net" />
        <meta property="og:image" content="https://geteat.net/og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GetEat" />
        <meta name="twitter:description" content="Create beautiful multilingual QR code menus for restaurants completely free." />
        <meta name="twitter:image" content="https://geteat.net/og-image.png" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'SoftwareApplication',
              name: 'GetEat',
              applicationCategory: 'BusinessApplication',
              operatingSystem: 'Web',
              offers: {
                '@type': 'Offer',
                price: '0',
                priceCurrency: 'USD',
              },
              description:
                'Free QR Code menu platform for restaurants with multilingual support.',
            }),
          }}
        />

        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl dark:border-white/10 dark:bg-[#0f172a]/80">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
            
            <div className="flex items-center gap-3">
              <img
                src="/images/logo/icon_square_128.png"
                alt="GetEat"
                className="flex"
                width={52}
                height={52}
              />
              <div className="flex flex-col">
                <h1 className="text-2xl font-black tracking-tight">
                  GetEat
                </h1>

                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Free QR Code menus generator for restaurants
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 self-start">

              <a
                href="/signin"
                className="rounded-xl border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Login
              </a>

              <a
                href="/signup"
                className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-600/30 transition hover:bg-indigo-500"
              >
                Register
              </a>
              <ThemeToggleButton />
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#4f46e520,transparent_35%)] dark:bg-[radial-gradient(circle_at_top_right,#4f46e540,transparent_35%)]" />

          <div className="relative mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:items-center lg:px-8 lg:py-28">
            <div>
              <div className="inline-flex items-center rounded-full border border-indigo-500/20 bg-indigo-500/10 px-4 py-1 text-sm font-medium text-indigo-600 dark:text-indigo-300">
                100% Free Restaurant QR Code Menu Platform
              </div>

              <h2 className="mt-7 text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Create Premium Digital QR Code Menus For Your Restaurant
              </h2>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
                Build multilingual restaurant menus with QR codes, sections, menu items, allergens, images — completely free.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="/signin"
                  className="rounded-2xl bg-indigo-600 px-8 py-4 text-base font-semibold text-white shadow-2xl shadow-indigo-600/30 transition hover:bg-indigo-500"
                >
                  Create Account
                </a>

                <a
                  href="#features"
                  className="rounded-2xl border border-slate-300 bg-white px-8 py-4 text-base font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                >
                  Explore Features
                </a>
              </div>

              <div className="mt-10 flex flex-wrap gap-6 text-sm font-medium text-slate-500 dark:text-slate-400">
                <div>✔ Multilingual Menus</div>
                <div>✔ Free QR Code Generator</div>
                <div>✔ Free Forever</div>
              </div>
            </div>

            <div className="relative w-full max-w-[400px] mx-auto lg:max-w-none">
              <div className="absolute -inset-6 rounded-[40px] bg-indigo-500/20 blur-3xl pointer-events-none dark:bg-indigo-500/30" />
              <div className="relative filter drop-shadow-[0_25px_25px_rgba(0,0,0,0.15)] dark:drop-shadow-[0_25px_25px_rgba(0,0,0,0.5)]">
                <img
                  src="/images/home_page/mobile-white.png"
                  alt="GetEat mobile white"
                  className="block w-auto h-[550px] mx-auto my-0 dark:hidden"
                />
                <img
                  src="/images/home_page/mobile-black.png"
                  alt="GetEat mobile black"
                  className="block w-auto h-[550px] mx-auto my-0 hidden dark:block"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section
          id="features"
          className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8"
        >
          <div className="max-w-3xl">
            <h3 className="text-3xl font-black sm:text-4xl">
              Everything Restaurants Need In One Platform
            </h3>

            <p className="mt-5 text-lg leading-8 text-slate-600 dark:text-slate-400">
              GetEat gives restaurants a premium digital menu experience with modern design, QR access, multilingual support.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-indigo-500/30 hover:shadow-2xl dark:border-white/10 dark:bg-[#111827]"
              >
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-500/10 text-3xl">
                  {feature.icon}
                </div>

                <h4 className="text-xl font-bold">
                  {feature.title}
                </h4>

                <p className="mt-4 leading-7 text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-br from-indigo-600 via-indigo-700 to-violet-700 px-6 py-16 text-center shadow-2xl shadow-indigo-600/20 sm:px-12 lg:px-16">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#ffffff30,transparent_55%)]" />

            <div className="relative">
              <h3 className="text-4xl font-black text-white sm:text-5xl">
                Launch Your Restaurant QR Code Menu Today
              </h3>

              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-indigo-100">
                Create your restaurant, build menus, generate QR codes.
              </p>

              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <a
                  href="/signup"
                  className="rounded-2xl bg-white px-8 py-4 text-base font-bold text-indigo-700 transition hover:bg-slate-100"
                >
                  Sign Up To Get Started
                </a>

                <a
                  href="/signin"
                  className="rounded-2xl border border-white/20 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:bg-white/20"
                >
                  Login
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
  )
}

export default LandingPage;
